import { motion } from 'motion/react';
import type { TradeStrategySummary } from '@/services/api';

type Props = {
  strategies: TradeStrategySummary[];
  onStrategyClick?: (strategy: TradeStrategySummary) => void;
  className?: string;
  maxVisible?: number;
};

function formatYtdPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function ytdTextClass(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return 'text-gray-600';
  if (value > 0) return 'text-emerald-700';
  if (value < 0) return 'text-rose-700';
  return 'text-gray-600';
}

function compositionLine(strategy: TradeStrategySummary): string | null {
  const parts: string[] = [];
  if (strategy.long_count > 0) parts.push(`${strategy.long_count} long`);
  if (strategy.short_count > 0) parts.push(`${strategy.short_count} short`);
  if (strategy.avoid_count != null && strategy.avoid_count > 0) {
    parts.push(`${strategy.avoid_count} avoid`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function TickerGroup({
  label,
  tickers,
  tone,
}: {
  label: string;
  tickers: string[];
  tone: 'long' | 'avoid';
}) {
  if (!tickers.length) return null;
  const labelClass = tone === 'long' ? 'text-emerald-700' : 'text-amber-700';
  return (
    <div className="flex items-start gap-2">
      <span className={`w-9 shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wide ${labelClass}`}>
        {label}
      </span>
      <div className="flex min-w-0 flex-wrap gap-1">
        {tickers.slice(0, 4).map((ticker) => (
          <span
            key={ticker}
            className="rounded-md bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-gray-200"
          >
            {ticker}
          </span>
        ))}
      </div>
    </div>
  );
}

export function TradeStrategyCards({
  strategies,
  onStrategyClick,
  className = '',
  maxVisible,
}: Props) {
  const visible = maxVisible != null ? strategies.slice(0, maxVisible) : strategies;
  if (visible.length === 0) return null;

  return (
    <div
      className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${className}`}
    >
      {visible.map((strategy, index) => {
        const CardWrapper = onStrategyClick ? 'button' : 'div';
        const composition = compositionLine(strategy);
        const ytd = strategy.ytd_performance?.ytd_return_pct;

        return (
          <motion.div
            key={strategy.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
          >
            <CardWrapper
              type={onStrategyClick ? 'button' : undefined}
              onClick={onStrategyClick ? () => onStrategyClick(strategy) : undefined}
              className={`flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 text-left transition-all duration-300 ${
                onStrategyClick ? 'group cursor-pointer hover:border-gray-900' : ''
              }`}
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-xs font-semibold text-white">
                    {strategy.rank}
                  </span>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold leading-snug text-gray-900 transition-transform group-hover:translate-x-0.5">
                      {strategy.strategy_name}
                    </h4>
                    {composition ? (
                      <p className="mt-0.5 text-[11px] text-gray-500">{composition}</p>
                    ) : null}
                  </div>
                </div>

                {ytd != null ? (
                  <div
                    className="shrink-0 text-right"
                    title={
                      strategy.ytd_performance?.ytd_benchmark_pct != null
                        ? `SPY ${formatYtdPct(strategy.ytd_performance.ytd_benchmark_pct)}`
                        : strategy.ytd_performance?.ytd_label ?? undefined
                    }
                  >
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                      {strategy.ytd_performance?.ytd_label ?? 'YTD'}
                    </p>
                    <p className={`text-sm font-semibold tabular-nums ${ytdTextClass(ytd)}`}>
                      {formatYtdPct(ytd)}
                    </p>
                  </div>
                ) : null}
              </div>

              <p className="mb-4 flex-1 text-xs leading-relaxed text-gray-600 line-clamp-3">
                {strategy.strategy_description || strategy.summary}
              </p>

              {strategy.tickers?.length || strategy.avoid_tickers?.length ? (
                <div className="mb-3 space-y-2 rounded-xl bg-gray-50 px-3 py-2.5">
                  <TickerGroup label="Long" tickers={strategy.tickers ?? []} tone="long" />
                  <TickerGroup
                    label="Avoid"
                    tickers={strategy.avoid_tickers ?? []}
                    tone="avoid"
                  />
                </div>
              ) : null}

              {strategy.tags?.length ? (
                <div className="flex flex-wrap gap-1.5 border-t border-gray-100 pt-3">
                  {strategy.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-gray-50 px-2 py-0.5 text-[10px] text-gray-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </CardWrapper>
          </motion.div>
        );
      })}
    </div>
  );
}
